import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ShieldCheck, MessageSquare, Ban } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { useSeo, faqJsonLd } from "@/lib/seo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const INCLUDED = [
  "Every official FSANZ recall, checked every 5 minutes",
  "Alerts filtered to your state so you only hear what applies",
  "Name your allergens and the text leads with them",
  "One SMS per recall - never a thread of follow-ups",
  "A link straight to the official notice, never a marketing page",
  "Reply STOP once and it ends. No call, no form",
];

const PLANS = [
  {
    key: "monthly",
    name: "Monthly",
    price: "A$4.99",
    per: "per month",
    note: "Cancel any time, no lock-in",
    sub: "About 16 cents a day",
  },
  {
    key: "yearly",
    name: "Yearly",
    price: "A$39.99",
    per: "per year",
    note: "That is nearly four months free",
    sub: "Just A$3.33 a month - save A$19.89 vs paying monthly",
    compareFrom: "A$59.88",
    savingsPct: 33,
    featured: true,
  },
];

const FAQS = [
  {
    q: "Why does this cost anything?",
    a: "Every alert is a real SMS and each one costs us money to send, whether you are home or halfway up a mountain with no data. Your subscription covers the messages and the monitoring behind them. Nothing here is funded by advertising or by selling your number.",
  },
  {
    q: "Can't I just check the FSANZ website?",
    a: "You can, and it is free. The catch is that it needs you to remember to look. Recalls are published without warning and the products are usually already in kitchens. This service exists so you find out without having to check anything.",
  },
  {
    q: "How many texts will I actually get?",
    a: "Australia averages roughly one food recall a week, and most people only match a fraction of them once state and allergen filters are applied. Typical households get a couple of texts a month, not a daily stream.",
  },
  {
    q: "Why SMS and not an app or email?",
    a: "A text arrives on every phone, needs no app, no login and no data connection, and it gets read. Recall information is only useful if it reaches you before the next meal.",
  },
  {
    q: "What happens to my number?",
    a: "It is used to send you alerts and nothing else. It is never sold, shared or used for marketing, and deleting your account removes it from our database.",
  },
  {
    q: "Can I stop whenever I want?",
    a: "Yes. Cancel from your settings, or reply STOP to any message. Monthly plans end at the end of the period you have paid for.",
  },
];

const FAQ_SCHEMA = faqJsonLd(FAQS);

export default function Pricing() {
  useSeo({
    title: "Pricing | Australian food recall SMS alerts from A$4.99",
    description:
      "Australian food recall alerts by SMS from A$4.99 a month or A$39.99 a year. One text per recall, filtered to your state and allergens, straight from the official FSANZ notice. Cancel any time.",
    path: "/pricing",
    jsonLd: FAQ_SCHEMA,
  });
  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="pointer-events-none absolute -right-32 -top-32 h-[26rem] w-[26rem] rounded-full bg-red-50 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 py-12 sm:py-16 lg:py-24">
          <motion.div initial="hidden" animate="show">
            <motion.p variants={fadeUp} custom={0} className="label-caps">Pricing</motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="mt-4 font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl"
            >
              Less than a coffee a month to know before you eat it.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 max-w-xl text-lg text-slate-600">
              Food recalls can happen at any moment. We make sure the ones that matter to you come to
              you instantly by SMS.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="mt-3 max-w-xl font-medium text-slate-900">
              One text per recall, filtered by your state and allergens.
            </motion.p>
            <motion.div variants={fadeUp} custom={4} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Official FSANZ data only</span>
              <span className="inline-flex items-center gap-2"><Ban className="h-4 w-4 text-slate-400" /> No ads, no data selling</span>
              <span className="inline-flex items-center gap-2"><MessageSquare className="h-4 w-4 text-slate-400" /> Reply STOP any time</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-5 py-12 sm:py-16 lg:py-20">
          <div className="grid gap-6 lg:grid-cols-2" data-testid="pricing-plans">
            {PLANS.map((p, i) => (
              <motion.div
                key={p.key}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                data-testid={`plan-card-${p.key}`}
                className={`relative rounded-3xl border p-6 transition-shadow sm:p-8 duration-300 hover:shadow-xl ${
                  p.featured ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-red-700 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="yearly-savings-badge">
                    Save {p.savingsPct}% - best value
                  </span>
                )}
                <p className={`label-caps ${p.featured ? "text-white/60" : ""}`}>{p.name}</p>
                {p.compareFrom && (
                  <p className={`mt-4 text-sm font-medium ${p.featured ? "text-white/60" : "text-slate-500"}`} data-testid="yearly-compare-from">
                    <span className="line-through">{p.compareFrom}</span>
                    <span className="ml-2 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-bold text-emerald-300">
                      {p.savingsPct}% off
                    </span>
                  </p>
                )}
                <p className={`${p.compareFrom ? "mt-1" : "mt-4"} flex flex-wrap items-baseline gap-2`}>
                  <span className="font-display text-5xl font-extrabold tracking-tighter">{p.price}</span>
                  <span className={p.featured ? "text-white/60" : "text-slate-500"}>{p.per}</span>
                </p>
                <p className={`mt-1 text-sm ${p.featured ? "text-white/60" : "text-slate-500"}`}>{p.sub}</p>
                <p className={`mt-5 text-sm font-medium ${p.featured ? "text-white/80" : "text-slate-700"}`}>{p.note}</p>
                <p className={`mt-1 text-xs ${p.featured ? "text-white/50" : "text-slate-500"}`}>
                  GST-inclusive - the price shown is the total you pay
                </p>
                <Link
                  to="/join"
                  data-testid={`plan-cta-${p.key}`}
                  className={`group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-colors ${
                    p.featured ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-red-700 text-white hover:bg-red-800"
                  }`}
                >
                  Get recall alerts
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-8"
            data-testid="pricing-included"
          >
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
              Both plans include everything
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {INCLUDED.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-slate-200 pt-4 text-sm text-slate-500">
              Browsing the full recall history on this site is free and always will be.{" "}
              <Link to="/alerts" data-testid="pricing-browse-link" className="font-medium text-slate-900 underline">
                Have a look first
              </Link>
              . You only pay for being told without having to check.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-5 py-12 sm:py-16 lg:py-20">
          <h2 className="font-display text-3xl font-extrabold tracking-tighter text-slate-900 sm:text-4xl">
            Fair questions
          </h2>
          <div className="mt-8 divide-y divide-slate-200" data-testid="pricing-faq">
            {FAQS.map((f, i) => (
              <motion.div
                key={f.q}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="py-6"
              >
                <h3 className="font-display text-base font-semibold text-slate-900 md:text-lg">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600 sm:text-base">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20">
          <h2 className="font-display text-3xl font-bold tracking-tighter text-white sm:text-5xl">
            You will not hear about the next one either.
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-white/70">
            Unless something is watching for you. Set it up once, in about a minute.
          </p>
          <Link
            to="/join"
            data-testid="pricing-closing-cta"
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-slate-900 transition-colors hover:bg-slate-100"
          >
            Get recall alerts
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <footer className="px-5 py-10">
        <div className="mx-auto max-w-6xl">
          <SourceNote />
        </div>
      </footer>
    </div>
  );
}
